/**
 * Typed access to the metric glossary.
 *
 * The JSON beside this file is the single source of truth for what every
 * habit metric means. The same entries feed the in-app ⓘ popovers, the
 * USER_GUIDE metric glossary, and the backend lock tests — so a formula can
 * only change in one place, and changing the implementation without changing
 * the glossary (or vice versa) fails a test rather than quietly misleading.
 */
import glossary from './metricsGlossary.json';

export interface MetricFacts {
  [fact: string]: boolean;
}

export interface MetricEntry {
  label: string;
  what: string;
  formula: string;
  window: string;
  denominator: string;
  includes_open_period: boolean;
  facts?: MetricFacts;
  weights?: Record<string, number>;
  min_periods?: number;
  max_periods?: number;
  lookback_days?: number;
}

export type MetricKey = keyof typeof glossary.metrics;

const METRICS = glossary.metrics as unknown as Record<string, MetricEntry>;

export const METRIC_KEYS = Object.keys(METRICS) as MetricKey[];

export const WINDOW_CONVENTIONS = glossary.window_conventions as Record<string, string>;

export interface MetricContext {
  /** Window length in days, substituted into {days} placeholders. */
  days?: number;
}

const substitute = (text: string, ctx?: MetricContext): string =>
  ctx?.days === undefined ? text : text.replace(/\{days\}/g, String(ctx.days));

/**
 * A glossary entry with placeholders resolved. Returns null for an unknown
 * key so a missing entry degrades to "no popover" rather than a crash.
 */
export const getMetricInfo = (
  key: string,
  ctx?: MetricContext,
): MetricEntry | null => {
  const entry = METRICS[key];
  if (!entry) {
    return null;
  }
  return {
    ...entry,
    what: substitute(entry.what, ctx),
    formula: substitute(entry.formula, ctx),
    window: substitute(entry.window, ctx),
    denominator: substitute(entry.denominator, ctx),
  };
};

/** Whether a metric counts the still-open current period. */
export const includesOpenPeriod = (key: string): boolean =>
  METRICS[key]?.includes_open_period ?? false;

export default METRICS;
